import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import {
  Broadcast, WarningCircle, GraduationCap, Bus, PersonSimpleRun,
  MapPin, CheckCircle, Clock, ArrowCounterClockwise, ArrowLeft,
  MagnifyingGlass, ClipboardText, Prohibit, HourglassMedium, XCircle,
} from '@phosphor-icons/react';
import Layout from '../../components/Layout';
import MapErrorBoundary from '../../components/MapErrorBoundary';
import { useAuth } from '../../context/AuthContext';
import securityService from '../../services/securityService';
import { animateMarkerTo, setMarkerHeading, stopMarkerAnimation, pushBreadcrumbPoint } from '../../utils/mapAnimation';
import './security.css';

const initials = (name) => (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

const timeAgo = (iso) => {
  if (!iso) return null;
  const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (diff < 60)   return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
};

const PIN_COLORS = {
  pending:             '#64748b',
  en_route_bus:        '#2563eb',
  en_route_collector:  '#f59e0b',
  arrived_unconfirmed: '#7c3aed',
  safe_confirmed:      '#16a34a',
};

const STATUS_LABELS = {
  pending:             'At School',
  en_route_bus:        'On Bus',
  en_route_collector:  'Collected',
  arrived_unconfirmed: 'Needs Confirm',
  safe_confirmed:      'Home Safe',
};

export default function AdminTrackingPage() {
  const navigate   = useNavigate();
  const { user }   = useAuth();

  const [students, setStudents]             = useState([]);
  const [busLocations, setBusLocations]     = useState({}); // routeId → {lat,lng}
  const [collectorLocs, setCollectorLocs]   = useState({}); // sessionToken → {lat,lng}
  const [loading, setLoading]               = useState(true);
  const [loadError, setLoadError]           = useState(null);
  const [filter, setFilter]                 = useState('all');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [viewMode, setViewMode]             = useState('map'); // 'map' | 'audit'
  const [scanLogs, setScanLogs]             = useState([]);
  const [logsLoading, setLogsLoading]       = useState(false);
  const [logsError, setLogsError]           = useState(null);
  const [logDays, setLogDays]               = useState(7);
  const [logResultFilter, setLogResultFilter] = useState('');

  const mapRef          = useRef(null);
  const mapInstanceRef  = useRef(null);
  const markersRef      = useRef({});      // studentId → L.marker
  const busMarkersRef   = useRef({});      // routeId → L.marker
  const colMarkersRef   = useRef({});      // sessionToken → L.marker
  const busTrailsRef    = useRef({});      // routeId → L.polyline (breadcrumb)
  const busPointsRef    = useRef({});      // routeId → [[lat,lng], ...]
  const colTrailsRef    = useRef({});      // sessionToken → L.polyline (breadcrumb)
  const colPointsRef    = useRef({});      // sessionToken → [[lat,lng], ...]
  const leafletRef      = useRef(null);

  // Lazily creates a bus marker + breadcrumb trail on its first real
  // position (never at a placeholder coordinate), then glides/rotates it on
  // every update after that.
  const upsertBusMarker = useCallback((routeId, lat, lng, heading) => {
    const L = leafletRef.current;
    const map = mapInstanceRef.current;
    if (!L || !map || lat == null || lng == null) return;

    if (!busMarkersRef.current[routeId]) {
      const busIcon = L.divIcon({
        html: `<div style="position:relative;width:28px;height:28px;">
          <div style="font-size:20px;line-height:1;filter:drop-shadow(0 2px 6px rgba(37,99,235,0.8));text-align:center;">🚌</div>
          <div class="map-heading-rotor" style="position:absolute;bottom:-4px;left:50%;margin-left:-6px;width:12px;height:12px;">
            <svg viewBox="0 0 12 12" width="12" height="12"><polygon points="6,0 11,11 6,8 1,11" fill="#2563eb" stroke="white" stroke-width="0.5"/></svg>
          </div>
        </div>`,
        className: '', iconSize: [28, 28], iconAnchor: [14, 14],
      });
      busMarkersRef.current[routeId] = L.marker([lat, lng], { icon: busIcon })
        .addTo(map)
        .bindTooltip('School Bus', { direction: 'top' });
      busPointsRef.current[routeId] = [[lat, lng]];
      busTrailsRef.current[routeId] = L.polyline([[lat, lng]], {
        color: '#2563eb', weight: 2, opacity: 0.45, dashArray: '4 6',
      }).addTo(map);
    } else {
      animateMarkerTo(busMarkersRef.current[routeId], [lat, lng]);
      setMarkerHeading(busMarkersRef.current[routeId], heading);
      busPointsRef.current[routeId] = pushBreadcrumbPoint(busPointsRef.current[routeId] || [], [lat, lng]);
      busTrailsRef.current[routeId]?.setLatLngs(busPointsRef.current[routeId]);
    }
  }, []);

  const removeBusMarker = useCallback((routeId) => {
    stopMarkerAnimation(busMarkersRef.current[routeId]);
    busMarkersRef.current[routeId]?.remove();
    busTrailsRef.current[routeId]?.remove();
    delete busMarkersRef.current[routeId];
    delete busTrailsRef.current[routeId];
    delete busPointsRef.current[routeId];
  }, []);

  // Same idea for the on-foot collector pin — no heading (a walking phone's
  // compass reading is too noisy to be worth showing).
  const upsertCollectorMarker = useCallback((token, lat, lng, studentName) => {
    const L = leafletRef.current;
    const map = mapInstanceRef.current;
    if (!L || !map || lat == null || lng == null) return;

    if (!colMarkersRef.current[token]) {
      const colIcon = L.divIcon({
        html: '<div style="font-size:20px;line-height:1;filter:drop-shadow(0 2px 6px rgba(245,158,11,0.8));">🏃</div>',
        className: '', iconSize: [22, 22], iconAnchor: [11, 22],
      });
      colMarkersRef.current[token] = L.marker([lat, lng], { icon: colIcon })
        .addTo(map)
        .bindTooltip(`Collector${studentName ? `: ${studentName}` : ''}`, { direction: 'top' });
      colPointsRef.current[token] = [[lat, lng]];
      colTrailsRef.current[token] = L.polyline([[lat, lng]], {
        color: '#f59e0b', weight: 2, opacity: 0.45, dashArray: '4 6',
      }).addTo(map);
    } else {
      animateMarkerTo(colMarkersRef.current[token], [lat, lng]);
      colPointsRef.current[token] = pushBreadcrumbPoint(colPointsRef.current[token] || [], [lat, lng]);
      colTrailsRef.current[token]?.setLatLngs(colPointsRef.current[token]);
    }
  }, []);

  const removeCollectorMarker = useCallback((token) => {
    stopMarkerAnimation(colMarkersRef.current[token]);
    colMarkersRef.current[token]?.remove();
    colTrailsRef.current[token]?.remove();
    delete colMarkersRef.current[token];
    delete colTrailsRef.current[token];
    delete colPointsRef.current[token];
  }, []);

  // Load overview from API
  const loadOverview = useCallback(async () => {
    if (!user?.school_id) return;
    try {
      const data = await securityService.getAdminOverview(user.school_id);
      setStudents(data.students || []);
      setLoadError(null);
    } catch (err) {
      setLoadError('Failed to load tracking data.');
    } finally {
      setLoading(false);
    }
  }, [user?.school_id]);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  // Poll every 20s
  useEffect(() => {
    const t = setInterval(loadOverview, 20000);
    return () => clearInterval(t);
  }, [loadOverview]);

  // Bus locations arrive primarily via the SSE push below; this poll is a
  // 30s reconciliation fallback in case a push is missed (reconnect gap, etc).
  useEffect(() => {
    const routeIds = [...new Set(
      students.filter(s => s.transport_route_id && s.arrival_status === 'en_route_bus')
               .map(s => s.transport_route_id)
    )];
    if (routeIds.length === 0) return;

    const fetchBusLocs = async () => {
      for (const routeId of routeIds) {
        try {
          const loc = await securityService.getTransportLocation(routeId);
          setBusLocations(prev => ({ ...prev, [routeId]: loc }));
          upsertBusMarker(routeId, loc.lat, loc.lng, loc.heading);
        } catch (_) {}
      }
    };
    fetchBusLocs();
    const t = setInterval(fetchBusLocs, 30000);
    return () => clearInterval(t);
  }, [students, upsertBusMarker]);

  // Live push: bus_location events over SSE update the map/state instantly
  useEffect(() => {
    if (!user?.school_id) return;
    let es;
    try {
      es = new EventSource(securityService.getEventsStreamUrl(user.school_id));
    } catch (err) {
      console.warn('SSE not available:', err);
      return;
    }

    es.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data.type === 'bus_location' && data.school_id === user.school_id) {
          setBusLocations(prev => ({ ...prev, [data.route_id]: data }));
          upsertBusMarker(data.route_id, data.lat, data.lng, data.heading);
        }
      } catch (err) {
        console.warn('Failed parsing SSE event', err);
      }
    };
    es.onerror = (err) => console.warn('SSE error', err);

    return () => { try { es.close(); } catch (_) {} };
  }, [user?.school_id, upsertBusMarker]);

  // Load scan audit log when that tab is active or its filters change
  const loadScanLogs = useCallback(async () => {
    if (!user?.school_id) return;
    setLogsLoading(true);
    setLogsError(null);
    try {
      const data = await securityService.getScanLogs(user.school_id, {
        days: logDays,
        result: logResultFilter || undefined,
        limit: 100,
      });
      setScanLogs(data.logs || []);
    } catch (err) {
      setLogsError('Failed to load scan log.');
    } finally {
      setLogsLoading(false);
    }
  }, [user?.school_id, logDays, logResultFilter]);

  useEffect(() => {
    if (viewMode === 'audit') loadScanLogs();
  }, [viewMode, loadScanLogs]);

  // Poll live collector locations
  useEffect(() => {
    const tokens = students
      .filter(s => s.collector_session_token && s.arrival_status === 'en_route_collector')
      .map(s => s.collector_session_token);
    if (tokens.length === 0) return;

    const fetchColLocs = async () => {
      for (const s of students) {
        const token = s.collector_session_token;
        if (!token || !tokens.includes(token)) continue;
        try {
          const loc = await securityService.getCollectorLiveLocation(token);
          if (loc.lat !== null) {
            setCollectorLocs(prev => ({ ...prev, [token]: loc }));
            upsertCollectorMarker(token, loc.lat, loc.lng, s.name);
          }
        } catch (_) {}
      }
    };
    fetchColLocs();
    const t = setInterval(fetchColLocs, 10000);
    return () => clearInterval(t);
  }, [students, upsertCollectorMarker]);

  // Init map
  useEffect(() => {
    if (mapInstanceRef.current || !mapRef.current) return;
    let isMounted = true;

    import('leaflet').then(L => {
      if (!isMounted || !mapRef.current || mapInstanceRef.current) return;

      leafletRef.current = L;

      const map = L.map(mapRef.current, { zoomControl: true, attributionControl: false })
        .setView([5.5560, -0.1870], 12);
      L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', { maxZoom: 19 }).addTo(map);

      const schoolIcon = L.divIcon({
        html: '<div style="font-size:22px;line-height:1;filter:drop-shadow(0 2px 6px rgba(59,130,246,0.8));">🏫</div>',
        className: '', iconSize: [28, 28], iconAnchor: [14, 14],
      });
      L.marker([5.5560, -0.1870], { icon: schoolIcon }).addTo(map)
        .bindTooltip('School', { permanent: false, direction: 'top' });

      mapInstanceRef.current = map;
    });

    return () => {
      isMounted = false;
      if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }
    };
  }, []);

  // Update student pins on map when data changes
  useEffect(() => {
    const L = leafletRef.current;
    if (!L || !mapInstanceRef.current) return;

    // Remove stale markers
    Object.keys(markersRef.current).forEach(id => {
      if (!students.find(s => s.id === id)) {
        markersRef.current[id]?.remove();
        delete markersRef.current[id];
      }
    });

    students.forEach(s => {
      if (!s.home_lat || !s.home_lng) return;
      const color = PIN_COLORS[s.arrival_status] || '#64748b';
      const icon = L.divIcon({
        html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 0 8px ${color};"></div>`,
        className: '', iconSize: [14, 14], iconAnchor: [7, 7],
      });

      if (markersRef.current[s.id]) {
        markersRef.current[s.id].setIcon(icon);
      } else {
        const marker = L.marker([s.home_lat, s.home_lng], { icon })
          .addTo(mapInstanceRef.current)
          .bindPopup(`
            <div style="font-family:sans-serif;min-width:160px;">
              <div style="font-weight:700;font-size:13px;">${s.name}</div>
              <div style="font-size:11px;color:#64748b;margin:2px 0;">${s.class || ''} · ${s.neighbourhood || ''}</div>
              ${s.ghana_post_address ? `<code style="font-size:11px;background:#f1f5f9;padding:2px 6px;border-radius:4px;">${s.ghana_post_address}</code>` : ''}
              <div style="font-size:12px;color:${color};font-weight:600;margin-top:4px;">${STATUS_LABELS[s.arrival_status] || s.arrival_status}</div>
            </div>
          `);
        markersRef.current[s.id] = marker;
      }
    });

    // Bus/collector markers themselves are created lazily by upsertBusMarker
    // / upsertCollectorMarker on their first real position (see the poll and
    // SSE effects) — never at a placeholder coordinate. This effect's job is
    // just cleanup: drop the marker + trail for any route/collector that's
    // no longer en route (pickup completed, or the status changed).
    const activeRouteIds = new Set(
      students.filter(s => s.transport_route_id && s.arrival_status === 'en_route_bus')
              .map(s => s.transport_route_id)
    );
    Object.keys(busMarkersRef.current).forEach(rid => {
      if (!activeRouteIds.has(rid)) removeBusMarker(rid);
    });

    const activeTokens = new Set(
      students.filter(s => s.collector_session_token && s.arrival_status === 'en_route_collector')
              .map(s => s.collector_session_token)
    );
    Object.keys(colMarkersRef.current).forEach(token => {
      if (!activeTokens.has(token)) removeCollectorMarker(token);
    });
  }, [students, removeBusMarker, removeCollectorMarker]);

  const focusStudent = (student) => {
    setSelectedStudent(student);
    if (mapInstanceRef.current && student.home_lat && student.home_lng) {
      mapInstanceRef.current.flyTo([student.home_lat, student.home_lng], 15, { animate: true, duration: 1 });
      markersRef.current[student.id]?.openPopup();
    }
  };

  const counts = {
    all:                  students.length,
    pending:              students.filter(s => s.arrival_status === 'pending').length,
    en_route_bus:         students.filter(s => s.arrival_status === 'en_route_bus').length,
    en_route_collector:   students.filter(s => s.arrival_status === 'en_route_collector').length,
    arrived_unconfirmed:  students.filter(s => s.arrival_status === 'arrived_unconfirmed').length,
    safe_confirmed:       students.filter(s => s.arrival_status === 'safe_confirmed').length,
    en_route:             students.filter(s => ['en_route_bus','en_route_collector'].includes(s.arrival_status)).length,
  };

  const filtered = students.filter(s =>
    filter === 'all' || s.arrival_status === filter
  );

  const fmtTime = (iso) => iso
    ? new Date(iso).toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit' })
    : '';

  return (
    <Layout>
    <div className="admin-track-page">
      {/* Header */}
      <div className="sec-header">
        <div className="sec-header-left">
          <div className="sec-shield-icon"><Broadcast size={20} /></div>
          <div>
            <p className="sec-header-title">Student Arrival Tracker</p>
            <p className="sec-header-sub">Admin View · All students</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="sec-btn-sm" style={{ padding: '8px 14px', fontSize: 12 }}
            onClick={() => setViewMode(viewMode === 'map' ? 'audit' : 'map')}>
            {viewMode === 'map' ? <><ClipboardText size={14} /> Audit Log</> : <><Broadcast size={14} /> Map View</>}
          </button>
          <button className="sec-btn-sm" style={{ padding: '8px 14px', fontSize: 12 }}
            onClick={viewMode === 'map' ? loadOverview : loadScanLogs}><ArrowCounterClockwise size={14} /> Refresh</button>
          <button className="sec-btn-sm" style={{ padding: '8px 14px', fontSize: 12 }}
            onClick={() => navigate('/security')}><ArrowLeft size={14} /> Hub</button>
        </div>
      </div>

      {/* Error */}
      {viewMode === 'map' && loadError && (
        <div style={{ margin: '8px 16px', padding: 10, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 12, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 6 }}>
          <WarningCircle size={14} /> {loadError}
          <button onClick={loadOverview} style={{ marginLeft: 8, textDecoration: 'underline', background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer' }}>Retry</button>
        </div>
      )}

      {viewMode === 'map' && (
      <>
      {/* Stats */}
      <div className="sec-stats-bar">
        {[
          { key: 'pending',            label: 'At School',     color: '#64748b' },
          { key: 'en_route',           label: 'En Route',      color: '#f59e0b' },
          { key: 'arrived_unconfirmed',label: 'Needs Confirm', color: '#7c3aed' },
          { key: 'safe_confirmed',     label: 'Home Safe',     color: '#16a34a' },
        ].map(({ key, label, color }) => (
          <div key={key} className="sec-stat"
            style={{ cursor: 'pointer', opacity: filter === key || filter === 'all' ? 1 : 0.5 }}
            onClick={() => setFilter(filter === key ? 'all' : key)}>
            <div className="sec-stat-value" style={{ color }}>
              {key === 'en_route' ? counts.en_route : counts[key] ?? 0}
            </div>
            <div className="sec-stat-label">{label}</div>
          </div>
        ))}
      </div>

      {/* Map */}
      <MapErrorBoundary minHeight={360}>
      <div style={{ position: 'relative' }}>
        <div id="admin-track-map" ref={mapRef} />
        <div className="admin-map-legend">
          {Object.entries(STATUS_LABELS).map(([key, label]) => (
            <div key={key} className="admin-legend-row">
              <div className="admin-legend-dot" style={{ background: PIN_COLORS[key] }} />
              <span>{label}</span>
            </div>
          ))}
          <div className="admin-legend-row"><span style={{ fontSize: 13 }}><Bus size={13} /></span><span>Bus (live)</span></div>
          <div className="admin-legend-row"><span style={{ fontSize: 13 }}><PersonSimpleRun size={13} /></span><span>Collector (live)</span></div>
        </div>
      </div>
      </MapErrorBoundary>

      {/* Filter bar */}
      <div className="admin-track-body">
        <div className="admin-filter-bar">
          {[
            { key: 'all',                 label: 'All',            icon: null },
            { key: 'pending',             label: 'At School',      icon: GraduationCap },
            { key: 'en_route_bus',        label: 'On Bus',         icon: Bus },
            { key: 'en_route_collector',  label: 'Collected',      icon: PersonSimpleRun },
            { key: 'arrived_unconfirmed', label: 'Needs Confirm',  icon: MapPin },
            { key: 'safe_confirmed',      label: 'Home Safe',      icon: CheckCircle },
          ].map(({ key, label, icon: Icon }) => (
            <button key={key}
              className={`admin-filter-btn ${filter === key ? 'active' : ''}`}
              onClick={() => setFilter(key)}>
              {Icon && <Icon size={14} />} {label}
              <span className="count">
                {key === 'all' ? counts.all : counts[key] ?? 0}
              </span>
            </button>
          ))}
        </div>

        {loading && (
          <div style={{ padding: 60, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
            Loading students...
          </div>
        )}

        {/* Student cards */}
        <div className="admin-student-list">
          {filtered.map(student => {
            const color = PIN_COLORS[student.arrival_status] || '#64748b';
            const isSelected = selectedStudent?.id === student.id;

            return (
              <div key={student.id}
                className={`admin-student-card ${student.arrival_status}`}
                style={{ outline: isSelected ? `2px solid ${color}` : 'none', cursor: 'pointer' }}
                onClick={() => focusStudent(student)}>
                <div className="admin-card-top">
                  <div className={`sec-avatar ${student.gender}`}>{initials(student.name)}</div>
                  <div style={{ flex: 1 }}>
                    <p className="sec-student-name">{student.name}</p>
                    <p className="sec-student-meta">
                      {student.class || 'Class N/A'} · {student.pickup_method === 'transport' ? 'School Bus' : 'Parent Pickup'}
                    </p>
                  </div>
                  <span className="sec-status-badge" style={{
                    color, background: color + '18', border: `1px solid ${color}33`,
                  }}>
                    {STATUS_LABELS[student.arrival_status] || student.arrival_status}
                  </span>
                </div>

                {student.ghana_post_address && (
                  <div>
                    <div className="admin-gps-pill"><MapPin size={12} /> {student.ghana_post_address}</div>
                    {student.neighbourhood && (
                      <div className="admin-neighbourhood">{student.neighbourhood}</div>
                    )}
                  </div>
                )}

                {/* Live bus/collector coords */}
                {student.arrival_status === 'en_route_bus' && busLocations[student.transport_route_id] && (
                  <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#2563eb', marginTop: 4 }}>
                    <Bus size={12} /> {busLocations[student.transport_route_id].lat?.toFixed(5)},&nbsp;
                    {busLocations[student.transport_route_id].lng?.toFixed(5)}
                    <span style={{ marginLeft: 6, color: '#9ca3af' }}>
                      · {timeAgo(busLocations[student.transport_route_id].recorded_at)}
                    </span>
                  </div>
                )}
                {student.arrival_status === 'en_route_collector' && student.collector_session_token
                  && collectorLocs[student.collector_session_token] && (
                  <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#f59e0b', marginTop: 4 }}>
                    <PersonSimpleRun size={12} /> {collectorLocs[student.collector_session_token].lat?.toFixed(5)},&nbsp;
                    {collectorLocs[student.collector_session_token].lng?.toFixed(5)}
                    <span style={{ marginLeft: 6, color: '#9ca3af' }}>
                      · {timeAgo(collectorLocs[student.collector_session_token].recorded_at)}
                    </span>
                  </div>
                )}

                {student.arrival_time && (
                  <div className="admin-arrival-time">
                    <span><Clock size={12} /></span>
                    <span>{fmtTime(student.arrival_time)}</span>
                    <span style={{ color: '#374151' }}>·</span>
                    <span>{timeAgo(student.arrival_time)}</span>
                    {student.confirmed_at && (
                      <>
                        <span style={{ color: '#374151' }}>·</span>
                        <span style={{ color: '#16a34a' }}>Confirmed {timeAgo(student.confirmed_at)}</span>
                      </>
                    )}
                  </div>
                )}

                {student.last_note && (
                  <div className="admin-note-preview">
                    "{student.last_note.text.length > 90
                      ? student.last_note.text.slice(0, 90) + '…'
                      : student.last_note.text}"
                    <div className="admin-note-author">
                      · {timeAgo(student.last_note.created_at)}
                    </div>
                  </div>
                )}

                <div className="sec-student-actions" onClick={e => e.stopPropagation()}>
                  {student.pickup_method === 'transport' && student.transport_route_id && (
                    <button className="sec-btn-sm"
                      onClick={() => navigate(`/security/driver?route=${student.transport_route_id}`)}>
                      <Bus size={14} /> Track Bus
                    </button>
                  )}
                  {student.collector_session_token && (
                    <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>
                      <Broadcast size={14} /> Collector active
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}><MagnifyingGlass size={40} /></div>
            <p>No students match this filter.</p>
          </div>
        )}

        <div style={{ padding: '8px 0 16px', fontSize: 11, color: '#9ca3af' }}>
          Ghana Post GPS addressing · Each code maps a 5 × 5m grid square
        </div>
      </div>
      </>
      )}

      {/* Audit Log */}
      {viewMode === 'audit' && (
        <div className="admin-track-body">
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '16px 0' }}>
            <select
              value={logDays}
              onChange={e => setLogDays(Number(e.target.value))}
              style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
            >
              <option value={1}>Today</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <select
              value={logResultFilter}
              onChange={e => setLogResultFilter(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
            >
              <option value="">All results</option>
              <option value="authorized">Authorized</option>
              <option value="unauthorized">Unauthorized</option>
              <option value="expired">Expired</option>
              <option value="already_used">Already used</option>
            </select>
            <span style={{ fontSize: 12, color: '#6b7280' }}>{scanLogs.length} scan{scanLogs.length !== 1 ? 's' : ''}</span>
          </div>

          {logsError && (
            <div style={{ margin: '0 0 16px', padding: 10, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 12, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 6 }}>
              <WarningCircle size={14} /> {logsError}
              <button onClick={loadScanLogs} style={{ marginLeft: 8, textDecoration: 'underline', background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer' }}>Retry</button>
            </div>
          )}

          {logsLoading && (
            <div style={{ padding: 60, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
              Loading scan log...
            </div>
          )}

          {!logsLoading && !logsError && scanLogs.length === 0 && (
            <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}><ClipboardText size={40} /></div>
              <p>No scans in this period.</p>
            </div>
          )}

          {!logsLoading && scanLogs.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {scanLogs.map(log => {
                const meta = {
                  authorized:    { color: '#16a34a', bg: '#f0fdf4', icon: CheckCircle,  label: 'Authorized' },
                  unauthorized:  { color: '#dc2626', bg: '#fef2f2', icon: Prohibit,     label: 'Unauthorized' },
                  expired:       { color: '#d97706', bg: '#fffbeb', icon: HourglassMedium, label: 'Expired' },
                  already_used:  { color: '#6b7280', bg: '#f3f4f6', icon: XCircle,      label: 'Already Used' },
                }[log.result] || { color: '#6b7280', bg: '#f3f4f6', icon: ClipboardText, label: log.result };
                const Icon = meta.icon;
                return (
                  <div key={log.id} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                    borderRadius: 12, border: '1px solid #e5e7eb', background: 'white',
                  }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%', background: meta.bg,
                      color: meta.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <Icon size={16} weight="fill" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: '#111827' }}>
                        {log.student_name || 'Unknown student'}
                      </p>
                      <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>
                        Scanned by {log.scanned_by_name}
                        {log.gate_lat && log.gate_lng && ` · ${log.gate_lat.toFixed(4)}, ${log.gate_lng.toFixed(4)}`}
                      </p>
                    </div>
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20,
                      background: meta.bg, color: meta.color, flexShrink: 0,
                    }}>
                      {meta.label}
                    </span>
                    <span style={{ fontSize: 11, color: '#9ca3af', flexShrink: 0, minWidth: 90, textAlign: 'right' }}>
                      {new Date(log.created_at).toLocaleString('en-GH', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
    </Layout>
  );
}
